import { useMemo, useState } from 'react';

export interface SortFieldDef<T> {
  field: string;
  label: string;
  getValue: (item: T) => string | number | null | undefined;
  defaultDir?: 'asc' | 'desc';
}

export interface SortState {
  field: string;
  dir: 'asc' | 'desc';
}

export interface UseClientSortResult<T> {
  sorted: T[];
  sort: SortState;
  handleSort: (field: string) => void;
}

/**
 * Client-side sorting over a pre-filtered array.
 * Caller owns filtering; this hook owns sort state and the sorted output.
 *
 * @param items   Already-filtered array to sort.
 * @param fields  Sort field definitions — determines available sort keys and getValue accessors.
 * @param defaultSort  Initial sort (defaults to first field, asc).
 */
export function useClientSort<T>(
  items: T[],
  fields: SortFieldDef<T>[],
  defaultSort?: SortState,
): UseClientSortResult<T> {
  const [sort, setSort] = useState<SortState>(
    defaultSort ?? { field: fields[0]?.field ?? '', dir: 'asc' },
  );

  const sorted = useMemo(() => {
    const def = fields.find((f) => f.field === sort.field);
    if (!def) return items;
    const mul = sort.dir === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const av = def.getValue(a) ?? '';
      const bv = def.getValue(b) ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return mul * (av - bv);
      return mul * String(av).localeCompare(String(bv));
    });
  }, [items, sort, fields]);

  function handleSort(field: string) {
    setSort((prev) => {
      if (prev.field === field) return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      const def = fields.find((f) => f.field === field);
      return { field, dir: def?.defaultDir ?? 'asc' };
    });
  }

  return { sorted, sort, handleSort };
}
